import React, { useState, useEffect } from 'react';
import { MapPin, Search, User, Hash } from 'lucide-react';
import apiService from './services/api';
import { Panel, Badge } from './components';

function CoveragePage() {
  const [zip, setZip] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [servedZips, setServedZips] = useState([]);
  const [servedLoading, setServedLoading] = useState(true);
  const [servedFilter, setServedFilter] = useState('');

  useEffect(() => {
    loadServedZips();
  }, []);

  const loadServedZips = async () => {
    try {
      const data = await apiService.getZipsServed();
      setServedZips(data.results || []);
    } catch { /* ignore */ }
    setServedLoading(false);
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!zip || zip.length < 5) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.zipLookup(zip);
      setResult(data);
    } catch (err) {
      setError(err.message || 'Zip code not found');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getDistanceColor = (miles) => {
    if (miles <= 15) return 'text-green-700 bg-green-50';
    if (miles <= 30) return 'text-blue-700 bg-blue-50';
    if (miles <= 60) return 'text-amber-700 bg-amber-50';
    return 'text-red-700 bg-red-50';
  };

  const getDistanceLabel = (miles) => {
    if (miles <= 15) return 'Nearby';
    if (miles <= 30) return 'In Range';
    if (miles <= 60) return 'Extended';
    return 'Out of Range';
  };

  const filteredServed = servedFilter
    ? servedZips.filter(z =>
        z.zip_code.startsWith(servedFilter) ||
        (z.city || '').toLowerCase().includes(servedFilter.toLowerCase()) ||
        (z.location || '').toLowerCase().includes(servedFilter.toLowerCase())
      )
    : servedZips;

  const totalAppts = servedZips.reduce((sum, z) => sum + z.appointment_count, 0);

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3 max-w-3xl mx-auto">
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900">Coverage Lookup</h1>
          <p className="text-xs text-gray-600 mt-0.5">Find which team members can serve a zip code</p>
        </div>

        <Panel className="mb-3">
          <form onSubmit={handleLookup} className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="Enter zip code..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
                maxLength={5}
              />
            </div>
            <button
              type="submit"
              disabled={loading || zip.length < 5}
              className="px-4 py-2 bg-red-800 text-white text-sm rounded-lg hover:bg-red-900 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {loading ? 'Searching...' : 'Search'}
            </button>
          </form>
        </Panel>

        {error && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <p className="text-xs text-red-800">{error}</p>
          </Panel>
        )}

        {result && (
          <>
            <Panel className="mb-3 bg-gray-50">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-red-800" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{result.city}, FL {result.zip}</p>
                  <p className="text-xs text-gray-500">{result.county} County</p>
                </div>
              </div>
            </Panel>

            {result.technicians.length === 0 ? (
              <Panel className="text-center py-8 mb-3">
                <p className="text-sm text-gray-500">No team members with zip codes assigned yet.</p>
                <p className="text-xs text-gray-400 mt-1">Add zip codes to team member profiles to enable coverage lookup.</p>
              </Panel>
            ) : (
              <div className="space-y-2 mb-3">
                {result.technicians.map((tech) => (
                  <Panel key={tech.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-red-800" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{tech.name}</p>
                        <p className="text-xs text-gray-500">{tech.location || 'No area'} · ZIP {tech.zip_code}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getDistanceColor(tech.distance_miles)}`}>
                        {tech.distance_miles} mi
                      </span>
                      <p className="text-xs text-gray-400 mt-0.5">{getDistanceLabel(tech.distance_miles)}</p>
                    </div>
                  </Panel>
                ))}
              </div>
            )}
          </>
        )}

        {/* Zips Served */}
        <Panel noPadding>
          <div className="px-3 py-2 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Hash className="w-3.5 h-3.5 text-gray-500" />
                <h2 className="text-xs font-medium text-gray-900">
                  Zip Codes Served ({servedZips.length} zips · {totalAppts.toLocaleString()} appointments)
                </h2>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                <input
                  type="text"
                  value={servedFilter}
                  onChange={(e) => setServedFilter(e.target.value)}
                  placeholder="Zip, city, or area..."
                  className="pl-6 pr-2 py-1 text-xs border border-gray-200 rounded w-40 focus:outline-none focus:ring-1 focus:ring-red-800"
                />
              </div>
            </div>
          </div>
          {servedLoading ? (
            <p className="text-xs text-gray-400 text-center py-6">Loading...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Zip</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">City</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Area</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Appts</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Nearest Tech</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Distance</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Last Served</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredServed.map((z) => (
                    <tr key={z.zip_code} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-xs font-mono font-medium text-gray-900">{z.zip_code}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-700">{z.city || '—'}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-500">{z.location || '—'}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-900 text-right font-semibold">{z.appointment_count}</td>
                      <td className="px-2 py-1.5 text-xs text-gray-700">{z.nearest_tech || '—'}</td>
                      <td className="px-2 py-1.5 text-xs text-right">
                        {z.nearest_distance != null ? (
                          <span className={`inline-block px-1.5 py-0.5 rounded font-medium ${
                            z.nearest_distance <= 15 ? 'text-green-700 bg-green-50' :
                            z.nearest_distance <= 30 ? 'text-blue-700 bg-blue-50' :
                            z.nearest_distance <= 60 ? 'text-amber-700 bg-amber-50' :
                            'text-red-700 bg-red-50'
                          }`}>{z.nearest_distance} mi</span>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-xs text-gray-500 text-right">
                        {z.last_appointment ? new Date(z.last_appointment).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredServed.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No zip codes match your filter</p>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

export default CoveragePage;
